import React, { useEffect, useMemo, useRef, useState } from "react";
import { alvoTour } from "../../tour";
import { BRL0, MIL, MESES, MESES_CURTOS } from "../formato";
import { centrarRolagem, curva, passoEixo, projecaoMensal, trechos } from "../calculos";
import { BlocoCarregando } from "./Carregando";

const W = 1240, H = 320, L = 84, R = 44, T = 30, B = 44;
const HALO = { paintOrder: "stroke", stroke: "var(--vg-surface)", strokeWidth: 4, strokeLinejoin: "round" };

/** FATURAMENTO MENSAL: ano atual, ano anterior tracejado, média e projeção.
 * O valor da média fica num cartãozinho no canto do cartão (dono, 23/10/2026) —
 * escrito dentro do gráfico ele encavalava com o rótulo de janeiro. */
export default function FaturamentoMensal({ dados }) {
  const calc = useMemo(() => (dados ? projecaoMensal({
    atual: dados.atual, anterior: dados.anterior, mesAtual: dados.mes_atual,
    diaHoje: dados.dia_hoje, diasNoMes: dados.dias_no_mes,
  }) : null), [dados]);
  return (
    <section className="vg-section" style={{ marginTop: 0 }}>
      <article ref={alvoTour("vg-mensal")} className="vg-card vg-mensal">
        <div className="vg-mensal-head">
          <h2>Faturamento mensal</h2>
          {calc?.media != null && (
            <div className="vg-kpi" title="Média dos meses já fechados deste ano (o mês atual não entra)">
              <span>Média mensal</span><b>{BRL0(calc.media)}</b>
            </div>
          )}
        </div>
        {!dados ? <BlocoCarregando altura={300} /> : <Grafico d={dados} calc={calc} />}
        <div className="vg-legend">
          <span><i />{dados?.ano ?? ""}</span><span><i className="ly" />{dados ? dados.ano - 1 : ""}</span>
          <span><i className="avg" />Média</span><span><i className="proj" />Projeção</span>
        </div>
      </article>
    </section>
  );
}

function Grafico({ d, calc }) {
  const { atual, anterior, mes_atual: iAtual } = d;
  const { inicio, media, proj } = calc;
  const [sel, setSel] = useState(null);
  const svgRef = useRef(null);
  const rolagem = useRef(null);
  useEffect(() => { centrarRolagem(rolagem.current, (L + iAtual * ((W - L - R) / 11)) / W); }, [iAtual]);

  if (inicio < 0 && !anterior.some((v) => v != null)) {
    return <div className="vg-vazio">Ainda não há vendas para montar o gráfico.</div>;
  }
  const ini = Math.max(0, inicio);
  const todos = [...atual, ...anterior, ...Object.values(proj)].filter((v) => v != null);
  const passo = passoEixo(Math.max(...todos, 1) * 1.12);
  const max = Math.ceil((Math.max(...todos, 1) * 1.12) / passo) * passo;
  const slot = (W - L - R) / 11;
  const x = (i) => L + i * slot;
  const y = (v) => T + (H - T - B) * (1 - v / max);
  const temDado = (i) => i >= ini && atual[i] != null;

  const grade = [];
  for (let v = 0; v <= max + 0.01; v += passo) grade.push(v);
  let iAnt = iAtual - 1;
  while (iAnt >= ini && atual[iAnt] == null) iAnt--;
  const linhaProj = [];
  if (iAnt >= ini && atual[iAnt] != null) linhaProj.push([x(iAnt), y(atual[iAnt])]);
  for (let i = iAtual; i < 12; i++) linhaProj.push([x(i), y(proj[i])]);

  return (
    <div ref={rolagem} className="vg-chart scroll">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Faturamento mensal"
        onMouseLeave={() => setSel(null)}>
        <defs>
          <linearGradient id="vg-gy" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--vg-brand)" stopOpacity=".2" />
            <stop offset="1" stopColor="var(--vg-brand)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {grade.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--vg-line-soft)" />
            <text x={L - 12} y={y(v) + 4} textAnchor="end" fontSize="11.5" fontWeight="500" fill="var(--vg-muted)">
              {v === 0 ? "R$ 0" : `R$ ${MIL(v)}`}
            </text>
          </g>
        ))}
        {ini > 0 && (
          <g>
            <rect x={L} y={T} width={x(ini) - slot / 2 - L} height={y(0) - T} fill="var(--vg-bg-2)" rx="6" />
            <text x={(L + x(ini) - slot / 2) / 2} y={(T + y(0)) / 2} textAnchor="middle" fontSize="11.5" fill="var(--vg-muted)">
              sem dados neste período
            </text>
          </g>
        )}
        {trechos(anterior, 0, 11, x, y).map((r, k) => (
          <path key={`a${k}`} d={curva(r)} fill="none" stroke="var(--vg-muted)" strokeWidth="1.5" strokeDasharray="5 6" strokeLinecap="round" />
        ))}
        {trechos(atual, ini, iAtual, x, y).map((r, k) => (
          <path key={`f${k}`} d={`${curva(r)} L${r[r.length - 1][0]} ${y(0)} L${r[0][0]} ${y(0)} Z`} fill="url(#vg-gy)" />
        ))}
        {media != null && (
          <g>
            <line x1={x(ini)} x2={W - R} y1={y(media)} y2={y(media)} stroke="var(--vg-good)" strokeWidth="1.5" strokeDasharray="2 6" />
          </g>
        )}
        <path d={curva(linhaProj)} fill="none" stroke="var(--vg-brand)" strokeWidth="2.5" strokeDasharray="3 7" strokeLinecap="round" opacity=".9" />
        {Object.keys(proj).map(Number).map((i) => (
          <g key={`p${i}`}>
            <circle cx={x(i)} cy={y(proj[i])} r="5" fill="var(--vg-surface)" stroke="var(--vg-brand)" strokeWidth="2.5" />
            <text x={x(i)} y={y(proj[i]) - 14} textAnchor="middle" fontSize="12.5" fontWeight="600" fill="var(--vg-brand)" style={HALO}>
              {MIL(proj[i])}
            </text>
          </g>
        ))}
        {trechos(atual, ini, iAtual, x, y).map((r, k) => (
          <path key={`l${k}`} d={curva(r)} fill="none" stroke="var(--vg-brand)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {atual.map((v, i) => (v == null || i < ini || i > iAtual ? null : (
          <g key={`v${i}`}>
            <circle cx={x(i)} cy={y(v)} r={i === iAtual ? 7 : 5.5} fill="var(--vg-brand)" stroke="var(--vg-surface)" strokeWidth="2.5" />
            <text x={x(i)} y={i < iAtual ? y(v) - 16 : y(v) + 24} textAnchor="middle" fontSize="13" fontWeight="700"
              fill={v === 0 ? "var(--vg-muted)" : "var(--vg-text)"} style={HALO}>
              {v === 0 && i < iAtual ? "fechado" : MIL(v)}
            </text>
          </g>
        )))}
        {MESES_CURTOS.map((t, i) => {
          const eAtual = i === iAtual, futuro = i > iAtual, sem = !temDado(i) && !futuro;
          return (
            <g key={t}>
              {eAtual && <rect x={x(i) - 22} y={H - 28} width="44" height="22" rx="11" fill="var(--vg-brand-soft)" />}
              <text x={x(i)} y={H - 13} textAnchor="middle" fontSize="12.5" fontWeight={eAtual ? 700 : 600}
                fill={eAtual ? "var(--vg-brand)" : futuro || sem ? "var(--vg-muted-2)" : "var(--vg-text-2)"}>
                {t.toUpperCase()}
              </text>
            </g>
          );
        })}
        {sel != null && <line x1={x(sel)} x2={x(sel)} y1={T} y2={y(0)} stroke="var(--vg-muted)" strokeDasharray="3 4" opacity=".6" />}
        {MESES_CURTOS.map((t, i) => (
          <rect key={`h${t}`} x={x(i) - slot / 2} y={T} width={slot} height={y(0) - T} fill="transparent" onMouseEnter={() => setSel(i)} />
        ))}
      </svg>
      {sel != null && <Dica i={sel} d={d} proj={proj} media={media} temDado={temDado} svg={svgRef.current} x={x} />}
    </div>
  );
}

function Dica({ i, d, proj, media, temDado, svg, x }) {
  const { atual, anterior, mes_atual: iAtual, dia_hoje: diaHoje, ano } = d;
  const r26 = atual[i], r25 = anterior[i];
  const k = svg ? svg.getBoundingClientRect().width / W : 1;
  const left = Math.max(100, Math.min((svg?.getBoundingClientRect().width || W) - 100, x(i) * k));
  const varia = (a, b) => { const v = Math.round((a / b - 1) * 100); return <b className={v >= 0 ? "good" : "bad"}>{v >= 0 ? "+" : ""}{v}%</b>; };
  const Mes = MESES[i][0].toUpperCase() + MESES[i].slice(1);
  let corpo;
  if (i <= iAtual && !temDado(i)) {
    corpo = <><h4>{Mes} <small>{ano}</small></h4><div><span>Sem dados neste mês</span></div></>;
  } else if (i <= iAtual) {
    corpo = (
      <>
        <h4>{Mes} <small>{ano}</small></h4>
        <div><span><i style={{ background: "var(--vg-brand)" }} />{ano}{i === iAtual ? ` (até dia ${diaHoje})` : ""}</span><b>{r26 === 0 ? "fechado" : BRL0(r26)}</b></div>
        {r25 != null && <div><span><i style={{ background: "var(--vg-muted)" }} />{ano - 1}</span><b>{BRL0(r25)}</b></div>}
        {r25 > 0 && r26 > 0 && <div className="var"><span>Variação</span>{varia(r26, r25)}</div>}
        {i === iAtual && <div className="proj"><span>Projeção de fechamento</span><b>{BRL0(proj[i])}</b></div>}
        {media != null && <div><span><i style={{ background: "var(--vg-good)" }} />Média</span><b>{BRL0(media)}</b></div>}
      </>
    );
  } else {
    corpo = (
      <>
        <h4>{Mes} <small>projeção</small></h4>
        <div className="proj"><span><i style={{ background: "var(--vg-brand)" }} />Projeção {ano}</span><b>{BRL0(proj[i])}</b></div>
        {r25 != null && <div><span><i style={{ background: "var(--vg-muted)" }} />{ano - 1}</span><b>{BRL0(r25)}</b></div>}
        {r25 > 0 && <div className="var"><span>Variação prevista</span>{varia(proj[i], r25)}</div>}
      </>
    );
  }
  return <div className="vg-tip" style={{ left, top: T * k - 8 }}>{corpo}</div>;
}
